import type { Vinculable, Vinculables } from '../api/estructura'

/**
 * Lo puro de «Vincular a…» (plan del CMS dinámico §7): de la ruta de un control a la ruta del
 * campo dentro de su bloque, qué expresiones caben en él, y cómo se lee y se escribe el mapa
 * `vinculos` del bloque. Sin React, para probarlo con objetos escritos a mano.
 *
 * Qué cabe dónde lo dice el núcleo (`cabeEn` de cada expresión y la clase de cada campo del
 * bloque); aquí solo se cruza. La puerta sigue siendo el guardado.
 */

/** Un vínculo guardado: la expresión sola o con un mapa (`{ expresion, mapa }`, para las listas). */
export type Vinculo = string | { expresion: string; mapa?: Record<string, unknown> }
export type MapaDeVinculos = Record<string, Vinculo>

/** «botones.0.etiqueta» → «botones.etiqueta»: la clase de un campo no depende de en qué fila está. */
export const rutaSinIndices = (ruta: string): string =>
  ruta
    .split('.')
    .filter((t) => !/^\d+$/.test(t))
    .join('.')

/** La ruta del campo dentro de su bloque, o `null` si el control no es de ese bloque (o es el propio bloque). */
export const rutaEnElBloque = (rutaDelControl: string, rutaDelBloque: string): null | string =>
  rutaDelControl.startsWith(`${rutaDelBloque}.`) ? rutaDelControl.slice(rutaDelBloque.length + 1) : null

export const expresionDe = (vinculo: unknown): null | string => {
  if (typeof vinculo === 'string') return vinculo
  if (vinculo && typeof vinculo === 'object' && typeof (vinculo as { expresion?: unknown }).expresion === 'string') return (vinculo as { expresion: string }).expresion
  return null
}

/** La clase de un campo del bloque (`text`, `upload`, `array`…), o `null` si no se puede vincular. */
export const claseDelCampo = (catalogo: Vinculables['bloques'] | undefined, bloque: string, rutaEnBloque: string): null | string =>
  catalogo?.[bloque]?.campos[rutaSinIndices(rutaEnBloque)] ?? null

/** Las expresiones que caben en un campo de esa clase. */
export const queCabenEn = (vinculables: Vinculable[], clase: null | string): Vinculable[] => (clase ? vinculables.filter((v) => v.cabeEn.includes(clase)) : [])

/** Agrupadas por `grupo`, en el orden en que llegan («Del documento», «Campos del tipo», «Taxonomías»…). */
export const agrupar = (vinculables: Vinculable[]): Array<{ grupo: string; opciones: Vinculable[] }> => {
  const grupos: Array<{ grupo: string; opciones: Vinculable[] }> = []
  for (const v of vinculables) {
    const grupo = grupos.find((g) => g.grupo === v.grupo)
    if (grupo) grupo.opciones.push(v)
    else grupos.push({ grupo: v.grupo, opciones: [v] })
  }
  return grupos
}

/** Cómo se nombra un vínculo en el panel: con la etiqueta que da el núcleo; si ya no está entre las que valen, la expresión tal cual. */
export const etiquetaDelVinculo = (vinculables: Vinculable[], expresion: string): string => vinculables.find((v) => v.expresion === expresion)?.etiqueta ?? expresion

/**
 * Los destinos del bloque que no son un campo que el panel pinte (`elementos` en
 * «relacionados», `ubicacion` en «mapa»): están en el catálogo de clases del núcleo y no
 * entre los campos del bloque. Se ofrecen aparte, al final del bloque.
 */
export const destinosSinCampo = (catalogo: Vinculables['bloques'] | undefined, bloque: string, nombresDeLosCampos: string[]): string[] =>
  Object.keys(catalogo?.[bloque]?.campos ?? {}).filter((ruta) => !ruta.includes('.') && !nombresDeLosCampos.includes(ruta))

/** Los destinos sin campo que hay hoy, con su nombre (y su tilde). */
const ROTULOS_DE_DESTINO: Record<string, string> = { elementos: 'Contenidos de la lista', ubicacion: 'Ubicación' }

/** «ubicacion» → «Ubicación»: el nombre de un destino sin campo, legible (el núcleo no le da rótulo). */
export const nombreDelDestino = (ruta: string): string => {
  const ultimo = ruta.split('.').pop() ?? ruta
  if (ROTULOS_DE_DESTINO[ultimo]) return ROTULOS_DE_DESTINO[ultimo]
  const conEspacios = ultimo.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return conEspacios.charAt(0).toUpperCase() + conEspacios.slice(1)
}

/** El mapa con un vínculo puesto o quitado, sin tocar el de entrada. */
export const conVinculo = (mapa: unknown, ruta: string, vinculo: null | Vinculo): MapaDeVinculos => {
  const actual: MapaDeVinculos = mapa && typeof mapa === 'object' && !Array.isArray(mapa) ? { ...(mapa as MapaDeVinculos) } : {}
  if (vinculo === null) delete actual[ruta]
  else actual[ruta] = vinculo
  return actual
}

/** Las clases de campo cuyo valor es texto: ahí la expresión se escribe también en el lugar del valor (§7.2), y un campo obligatorio no queda vacío. */
export const CLASES_DE_TEXTO = new Set(['text', 'textarea'])

/**
 * De un rechazo del núcleo al guardar una plantilla (`problemas: [{ clave, mensaje }]`, la
 * clave como `plantillaFicha.0.vinculos.titulo`) a errores por ruta, para pintarlos junto a su
 * campo y marcar su bloque.
 */
export const erroresDeProblemas = (datos: unknown): Record<string, string> => {
  const problemas = datos && typeof datos === 'object' ? (datos as { problemas?: unknown }).problemas : undefined
  if (!Array.isArray(problemas)) return {}
  const salida: Record<string, string> = {}
  for (const p of problemas as Array<{ clave?: unknown; mensaje?: unknown }>) {
    if (typeof p.clave === 'string' && typeof p.mensaje === 'string') salida[p.clave] = p.mensaje
  }
  return salida
}
